import { FC, useCallback, useMemo } from "react";
import { PopoverOrigin } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { CommitmentOrganizationSelect } from "@/components/common/commitment-org-select/CommitmentOrganizationSelect";
import { WidgetQuickActionTrigger } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/WidgetQuickActionTrigger";
import { updateCommitmentUtilizationWidgetFromQuickActionsThunk } from "@/store/dashboards/thunks/widgets/commitment-utilization/actions/updateCommitmentUtilizationWidgetFromQuickActionsThunk";
import { useAppAbility } from "@/services/permissions";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { CommitmentCloudProvider } from "@/services/cloudchipr.api";
import { OrgDataChip } from "@/components/pages/dashboard/components/widgets/common/widget-header/source-and-name/OrgDataChip";

interface CommitmentUtilizationWidgetHeaderOrgIdSelectProps {
  widgetId?: string;
  provider?: CommitmentCloudProvider;
  providerOrganisationId?: string;
}

export const CommitmentUtilizationWidgetHeaderOrgIdSelect: FC<
  CommitmentUtilizationWidgetHeaderOrgIdSelectProps
> = ({ widgetId, providerOrganisationId, provider }) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotEditWidget = cannot("edit", "dashboard");
  const isProtected = useAppSelector(currentDashboardProtectedSelector);

  const selectedOrg = useMemo(
    () => ({
      organizationId: providerOrganisationId,
      provider: provider ?? "aws",
    }),
    [providerOrganisationId, provider],
  );

  const handleChange = useCallback(
    (provider: CommitmentCloudProvider, providerOrganisationId: string) => {
      dispatch(
        updateCommitmentUtilizationWidgetFromQuickActionsThunk({
          id: widgetId,
          provider_organisation_id: providerOrganisationId,
          provider,
        }),
      );
    },
    [dispatch, widgetId],
  );

  return (
    <OrgDataChip
      providerOrganisationId={providerOrganisationId}
      provider={provider}
    >
      {widgetId && !canNotEditWidget && !isProtected && (
        <CommitmentOrganizationSelect
          onChange={handleChange}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          selectedOrg={selectedOrg}
          CustomTriggerComponent={WidgetQuickActionTrigger}
        />
      )}
    </OrgDataChip>
  );
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};
